import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { OFFICE_LOCATIONS } from '../constants/officeLocations';

export interface CalendarEvent {
  id: string;
  calendar_id: string;
  title: string;
  description?: string;
  start_time: string;
  end_time: string;
  all_day: boolean;
  location?: string;
  meeting_link?: string;
  meeting_type?: 'google_meet' | 'zoom' | 'teams' | 'phone' | 'in_person' | 'online';
  status: 'confirmed' | 'tentative' | 'cancelled';
  event_theme?: string;
  guest_email?: string;
  color?: string;
  guest_name?: string;
  guest_surname?: string;
  guest_class?: string;
  guest_phone?: string;
  interest_department?: string;
  notes?: string;
  visibility: 'public' | 'private' | 'default';
  created_by: string;
  created_at: string;
  updated_at: string;
  attendees?: EventAttendee[];
  calendar?: Calendar;
}

export interface EventAttendee {
  id: string;
  event_id: string;
  user_id?: string;
  email?: string;
  name?: string;
  response_status: 'accepted' | 'declined' | 'tentative' | 'needs_action';
  is_organizer: boolean;
  can_modify: boolean;
  can_invite_others: boolean;
  can_see_other_guests: boolean;
}

export interface Calendar {
  id: string;
  name: string;
  description?: string;
  color: string;
  owner_id: string;
  type: 'personal' | 'shared' | 'team';
  is_default: boolean;
  is_visible: boolean;
  timezone: string;
  created_at: string;
  updated_at: string;
}

export const useCalendar = () => {
  const { user } = useAuth();
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [calendars, setCalendars] = useState<Calendar[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch user's calendars
  const fetchCalendars = async () => {
    if (!user?.id) return;

    setLoading(true);
    setError(null);

    try {
      // Fetch calendars where user is owner
      const { data: ownedCalendars, error: ownedError } = await supabase
        .from('calendars')
        .select('*')
        .eq('owner_id', user.id)
        .order('name');

      if (ownedError) throw ownedError;

      // Fetch shared calendars where user has permissions
      const { data: sharedCalendars, error: sharedError } = await supabase
        .from('calendars')
        .select(`
          *,
          calendar_permissions!inner(permission_level)
        `)
        .eq('calendar_permissions.user_id', user.id)
        .neq('owner_id', user.id)
        .order('name');

      if (sharedError) throw sharedError;

      // Combine owned and shared calendars
      const allCalendars = [
        ...(ownedCalendars || []),
        ...(sharedCalendars || [])
      ];

      setCalendars(allCalendars);
    } catch (err) {
      console.error('Error fetching calendars:', err);
      setError(err instanceof Error ? err.message : 'Takvimler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Fetch events for user's calendars
  const fetchEvents = async () => {
    if (!user?.id) return;

    setLoading(true);
    setError(null);

    try {
      // Get calendar IDs user has access to
      const sharedCalendarIds = calendars.filter(c => c.owner_id !== user.id).map(c => c.id);

      let query = supabase
        .from('calendars')
        .select('id');

      // Build the query conditionally
      if (sharedCalendarIds.length > 0) {
        query = query.or(`owner_id.eq.${user.id},id.in.(${sharedCalendarIds.join(',')})`);
      } else {
        query = query.eq('owner_id', user.id);
      }

      const { data: accessibleCalendars, error: calendarError } = await query;
      if (calendarError) throw calendarError;

      const calendarIds = accessibleCalendars?.map(c => c.id) || [];
      if (calendarIds.length === 0) {
        setEvents([]);
        return;
      }

      const { data, error } = await supabase
        .from('calendar_events')
        .select(`
          *,
          calendar:calendars!inner(id, name, color)
        `)
        .in('calendar_id', calendarIds)
        .order('start_time');

      if (error) throw error;

      // Transform data to include attendees properly (mock for now to fix 500 error)
      const eventsWithAttendees = (data || []).map(event => ({
        ...event,
        attendees: []
      }));

      setEvents(eventsWithAttendees);
    } catch (err) {
      console.error('Error fetching events:', err);
      setError(err instanceof Error ? err.message : 'Etkinlikler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Create new calendar
  const createCalendar = async (calendarData: Partial<Calendar> & { shares?: { userId: string, permissionLevel: string }[] }) => {
    if (!user?.id) return { data: null, error: 'Kullanıcı bulunamadı' };

    try {
      const { shares, ...restCalendarData } = calendarData;

      const { data, error } = await supabase
        .from('calendars')
        .insert([{
          ...restCalendarData,
          owner_id: user.id
        }])
        .select()
        .single();

      if (error) throw error;

      // Handle shares if present
      if (shares && shares.length > 0 && data) {
        const permissionsData = shares.map(share => ({
          calendar_id: data.id,
          user_id: share.userId,
          permission_level: share.permissionLevel,
          can_create_events: share.permissionLevel === 'editor' || share.permissionLevel === 'admin',
          can_edit_events: share.permissionLevel === 'editor' || share.permissionLevel === 'admin',
          can_delete_events: share.permissionLevel === 'admin',
          can_invite_others: share.permissionLevel === 'admin'
        }));

        const { error: permissionsError } = await supabase
          .from('calendar_permissions')
          .insert(permissionsData);

        if (permissionsError) {
          console.error('Error adding permissions:', permissionsError);
          // We don't throw here to avoid failing the whole calendar creation, but we log it
        }
      }

      setCalendars(prev => [...prev, data]);
      return { data, error: null };
    } catch (err) {
      console.error('Error creating calendar:', err);
      return { data: null, error: err instanceof Error ? err.message : 'Takvim oluşturulurken hata oluştu' };
    }
  };

  // Create new event with Google Meet integration
  const createEvent = async (eventData: any) => {
    if (!user?.id) return { data: null, error: 'Kullanıcı bulunamadı' };

    try {
      let meetingLink = eventData.meeting_link;

      // Generate Google Meet link if requested
      if (eventData.createGoogleMeet) {
        meetingLink = await generateGoogleMeetLink(eventData);
      }

      const { data, error } = await supabase
        .from('calendar_events')
        .insert([{
          calendar_id: eventData.calendar_id,
          title: eventData.title,
          description: eventData.description,
          start_time: eventData.start_time,
          end_time: eventData.end_time,
          all_day: eventData.all_day || false,
          location: eventData.location,
          meeting_link: meetingLink,
          meeting_type: eventData.createGoogleMeet ? 'google_meet' : eventData.meeting_type,
          status: eventData.status || 'confirmed',
          visibility: eventData.visibility || 'default',
          created_by: user.id,
          event_theme: eventData.event_theme,
          guest_name: eventData.guest_name,
          guest_surname: eventData.guest_surname,
          guest_class: eventData.guest_class,
          guest_phone: eventData.guest_phone,
          interest_department: eventData.interest_department,
          notes: eventData.notes
        }])
        .select()
        .single();

      if (error) throw error;

      // Add attendees
      if (eventData.attendees && Array.isArray(eventData.attendees) && eventData.attendees.length > 0) {
        const attendeesData = eventData.attendees.map((attendee: any) => ({
          event_id: data.id,
          user_id: attendee.id && !attendee.id.startsWith('external_') ? attendee.id : null,
          email: attendee.email,
          name: attendee.name,
          response_status: 'needs_action',
          is_organizer: false,
          can_modify: false,
          can_invite_others: false,
          can_see_other_guests: true
        }));

        // Add organizer as attendee
        attendeesData.push({
          event_id: data.id,
          user_id: user.id,
          email: user.email || '',
          name: user.name || '',
          response_status: 'accepted',
          is_organizer: true,
          can_modify: true,
          can_invite_others: true,
          can_see_other_guests: true
        });

        const { error: attendeesError } = await supabase
          .from('event_attendees')
          .insert(attendeesData);

        if (attendeesError) {
          console.error('Katılımcı ekleme hatası:', attendeesError);
        }

        // Send email invitations if requested
        if (eventData.sendEmailInvites) {
          try {
            await sendEventInvitations(data, eventData.attendees, eventData.emailMessage, user.id);
          } catch (emailError) {
            console.error('Email gönderme hatası:', emailError);
            // Email hatası etkinlik oluşturmayı engellemez
          }
        }
      }

      // Refresh events
      await fetchEvents();

      // Send SMS if guest phone is provided
      if (data && eventData.guest_phone) {
        try {
          const selectedLocation = OFFICE_LOCATIONS.find(l => l.name === eventData.location);

          let smsMessage = '';

          if (eventData.meeting_type === 'online' || eventData.event_theme === 'Online') {
            smsMessage = `Merhabalar, Pisa Egitim Merkez Ofis icin randevunuz olusturulmustur. Belirlenen tarih ve saatte katiliminizi rica ederiz.\n\nTarih: ${new Date(eventData.start_time).toLocaleDateString('tr-TR')}\nSaat: ${new Date(eventData.start_time).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}\nGorusmelerimiz 45 dk.'dir.\nGorusme linki: ${data.meeting_link || 'Daha sonra iletilecektir'}\n\nHerhangi bir degisiklik durumunda 0212 544 24 15 numarali hattimizdan bize bilgi vermenizi rica ederiz.\nTesekkur eder, iyi gunler dileriz.`;
          } else {
            // Physical meeting
            const locName = selectedLocation ? selectedLocation.name : (eventData.location || 'Ofis');
            const locAddress = selectedLocation ? selectedLocation.address : 'Istanbul Skyland Merkez Ofis';
            const locMap = selectedLocation ? selectedLocation.mapsLink : 'https://maps.app.goo.gl/QVgUXpgjABqRFVcu5';
            const locPhone = selectedLocation ? selectedLocation.phone : '0212 544 24 15';

            smsMessage = `Merhabalar, Pisa Egitim ${locName} icin randevunuz olusturulmustur. Belirlenen tarih ve saatte katiliminizi rica ederiz.\n\nTarih: ${new Date(eventData.start_time).toLocaleDateString('tr-TR')}\nSaat: ${new Date(eventData.start_time).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}\nGorusmelerimiz 45 dk.'dir.\nAdres: ${locAddress}\nKonum: ${locMap}\n\nHerhangi bir degisiklik durumunda ${locPhone} numarali hattimizdan bize bilgi vermenizi rica ederiz.\nTesekkur eder, iyi gunler dileriz.`;
          }

          if (eventData.event_theme === 'Online' || eventData.event_theme === 'Fiziki') {
            await supabase.functions.invoke('send-sms', {
              body: {
                phones: [eventData.guest_phone],
                message: smsMessage,
                userId: user.id,
                branchId: user.branchId
              }
            });
          }
        } catch (smsError) {
          console.error('Randevu SMS gönderim hatası:', smsError);
        }
      }

      return { data, error: null };
    } catch (err) {
      console.error('Error creating event:', err);
      return { data: null, error: err instanceof Error ? err.message : 'Etkinlik oluşturulurken hata oluştu' };
    }
  };

  // Update event
  const updateEvent = async (eventId: string, updates: Partial<CalendarEvent>) => {
    try {
      const { data, error } = await supabase
        .from('calendar_events')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', eventId)
        .select()
        .single();

      if (error) throw error;

      setEvents(prev => prev.map(e => e.id === eventId ? { ...e, ...data } : e));

      // Send SMS update if phone is present in updates or existing event
      const phoneToNotify = updates.guest_phone || (data?.guest_phone);
      // Only send if Theme is Online or Fiziki
      const themeToCheck = updates.event_theme || data?.event_theme;

      if (data && phoneToNotify && (themeToCheck === 'Online' || themeToCheck === 'Fiziki')) {
        try {
          const locationName = updates.location || data.location;
          const selectedLocation = OFFICE_LOCATIONS.find(l => l.name === locationName);

          let smsMessage = '';

          if (themeToCheck === 'Online') {
            smsMessage = `Merhabalar, Pisa Egitim randevunuz GÜNCELLENMİŞTİR.\n\nYeni Tarih: ${new Date(data.start_time).toLocaleDateString('tr-TR')}\nYeni Saat: ${new Date(data.start_time).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}\nGorusmelerimiz 45 dk.'dir.\nGorusme linki: ${data.meeting_link || 'Daha sonra iletilecektir'}\n\nHerhangi bir degisiklik durumunda 0212 544 24 15 numarali hattimizdan bize bilgi vermenizi rica ederiz.\nTesekkur eder, iyi gunler dileriz.`;
          } else {
            // Physical meeting update
            const locName = selectedLocation ? selectedLocation.name : (locationName || 'Ofis');
            const locAddress = selectedLocation ? selectedLocation.address : 'Istanbul Skyland Merkez Ofis';
            const locMap = selectedLocation ? selectedLocation.mapsLink : 'https://maps.app.goo.gl/QVgUXpgjABqRFVcu5';
            const locPhone = selectedLocation ? selectedLocation.phone : '0212 544 24 15';

            smsMessage = `Merhabalar, Pisa Egitim randevunuz GÜNCELLENMİŞTİR.\n\nYeni Tarih: ${new Date(data.start_time).toLocaleDateString('tr-TR')}\nYeni Saat: ${new Date(data.start_time).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}\nGorusmelerimiz 45 dk.'dir.\nAdres: ${locAddress}\nKonum: ${locMap}\n\nHerhangi bir degisiklik durumunda ${locPhone} numarali hattimizdan bize bilgi vermenizi rica ederiz.\nTesekkur eder, iyi gunler dileriz.`;
          }

          await supabase.functions.invoke('send-sms', {
            body: {
              phones: [phoneToNotify],
              message: smsMessage,
              userId: user?.id,
              branchId: user?.branchId
            }
          });
        } catch (smsError) {
          console.error('Randevu güncelleme SMS gönderim hatası:', smsError);
        }
      }

      return { data, error: null };
    } catch (err) {
      console.error('Error updating event:', err);
      return { data: null, error: err instanceof Error ? err.message : 'Etkinlik güncellenirken hata oluştu' };
    }
  };

  // Delete event
  const deleteEvent = async (eventId: string) => {
    try {
      const { error } = await supabase
        .from('calendar_events')
        .delete()
        .eq('id', eventId);

      if (error) throw error;

      setEvents(prev => prev.filter(e => e.id !== eventId));
      return { error: null };
    } catch (err) {
      console.error('Error deleting event:', err);
      return { error: err instanceof Error ? err.message : 'Etkinlik silinirken hata oluştu' };
    }
  };

  // Update attendee response
  const updateAttendeeResponse = async (eventId: string, responseStatus: string) => {
    if (!user?.id) return { error: 'Kullanıcı bulunamadı' };

    try {
      const { error } = await supabase
        .from('event_attendees')
        .update({
          response_status: responseStatus,
          updated_at: new Date().toISOString()
        })
        .eq('event_id', eventId)
        .eq('user_id', user.id);

      if (error) throw error;

      // Refresh events to show updated response
      await fetchEvents();
      return { error: null };
    } catch (err) {
      console.error('Error updating attendee response:', err);
      return { error: err instanceof Error ? err.message : 'Yanıt güncellenirken hata oluştu' };
    }
  };

  // Share calendar with user
  const shareCalendar = async (calendarId: string, userId: string, permissionLevel: string) => {
    try {
      const { data, error } = await supabase
        .from('calendar_permissions')
        .insert([{
          calendar_id: calendarId,
          user_id: userId,
          permission_level: permissionLevel,
          can_create_events: permissionLevel === 'editor',
          can_edit_events: permissionLevel === 'editor',
          can_delete_events: false,
          can_invite_others: permissionLevel === 'editor'
        }])
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('Error sharing calendar:', err);
      return { data: null, error: err instanceof Error ? err.message : 'Takvim paylaşılırken hata oluştu' };
    }
  };

  // Manual SMS Reminder
  const sendManualReminder = async (event: CalendarEvent) => {
    try {
      if (!event.guest_phone) throw new Error('Misafir telefonu bulunamadı');

      const selectedLocation = OFFICE_LOCATIONS.find(l => l.name === event.location);
      const officePhone = selectedLocation ? selectedLocation.phone : '0212 544 24 15';
      const formattedDate = new Date(event.start_time).toLocaleDateString('tr-TR');
      const formattedTime = new Date(event.start_time).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });

      const smsMessage = `Merhabalar, Pisa Egitim'den iletisime geciyoruz. Bugun gerceklesecek olan gorusmenizi hatirlatmak isteriz.\n\nTarih: ${formattedDate}\nSaat: ${formattedTime}\nGorusmelerimiz 45 dk.'dir.\nHerhangi bir degisiklik durumunda ${officePhone} numarali hattimizdan bize bilgi vermenizi rica ederiz.\nTesekkur eder, iyi gunler dileriz.`;

      const { data: userData } = await supabase.auth.getUser();

      await supabase.functions.invoke('send-sms', {
        body: {
          phones: [event.guest_phone],
          message: smsMessage,
          userId: userData.user?.id,
          branchId: ''
        }
      });

      return { success: true, error: null };
    } catch (err) {
      console.error('Error sending manual reminder:', err);
      return { success: false, error: err instanceof Error ? err.message : 'SMS gönderilirken hata oluştu' };
    }
  };

  // Initialize calendars and events
  useEffect(() => {
    if (user?.id) {
      fetchCalendars();
    }
  }, [user?.id]);

  useEffect(() => {
    if (calendars.length > 0) {
      fetchEvents();
    }
  }, [calendars]);

  return {
    events,
    calendars,
    loading,
    error,
    fetchCalendars,
    fetchEvents,
    createCalendar,
    createEvent,
    updateEvent,
    deleteEvent,
    updateAttendeeResponse,
    shareCalendar
  };
};

// Email invitation sending function
const sendEventInvitations = async (event: any, attendees: any[], customMessage?: string, organizerUserId?: string) => {
  try {
    if (!organizerUserId) {
      console.warn('Organizer user ID not provided, skipping email invitations');
      return;
    }

    // Call Supabase Edge Function to send invitations
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    if (!supabaseUrl || !supabaseAnonKey) {
      console.error('Supabase environment variables not found');
      return;
    }

    const functionUrl = `${supabaseUrl}/functions/v1/send-calendar-invitation`;

    // Get current session for authorization
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      console.error('No active session found');
      return;
    }

    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
        'apikey': supabaseAnonKey
      },
      body: JSON.stringify({
        eventData: event,
        attendees: attendees,
        customMessage: customMessage,
        organizerUserId: organizerUserId
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Email invitation error:', errorData);
      alert(`Email gönderiminde hata: ${errorData.error || 'Bilinmeyen hata'}`);
      return;
    }

    const result = await response.json();

    if (result.success) {
      alert(`${result.sentCount}/${result.totalAttendees} katılımcıya email daveti gönderildi!`);
    } else {
      alert(`Email gönderiminde hata: ${result.error}`);
    }

  } catch (error) {
    console.error('Email invitation error:', error);
    alert('Email daveti gönderilirken hata oluştu.');
  }
};

// Google Meet link generation function
export const generateGoogleMeetLink = async (eventData: any): Promise<string> => {
  try {
    // For now, generate a mock Google Meet link
    // In production, you would use Google Calendar API to create actual meet links
    const meetingId = Math.random().toString(36).substr(2, 10);
    const meetLink = `https://meet.google.com/${meetingId}`;

    console.log('Google Meet link generated:', meetLink);

    // TODO: Implement actual Google Meet API integration
    // This would require:
    // 1. Google Calendar API credentials
    // 2. OAuth 2.0 authentication
    // 3. Calendar event creation with conferenceData

    return meetLink;
  } catch (error) {
    console.error('Error generating Google Meet link:', error);
    throw new Error('Google Meet linki oluşturulamadı');
  }
};

// Get user's default calendar
export const getUserDefaultCalendar = async (userId: string): Promise<Calendar | null> => {
  try {
    const { data, error } = await supabase
      .from('calendars')
      .select('*')
      .eq('owner_id', userId)
      .eq('is_default', true)
      .single();

    if (error) throw error;
    return data;
  } catch (err) {
    console.error('Error fetching default calendar:', err);
    return null;
  }
};